/**
 * 96. Integration Center — connectors; auth; logs; retries; health;
 * onboarding. Connectors cover banks, payments, e-commerce, POS, shipping,
 * tax authorities, e-invoicing, CRM/HR/payroll, email/SMS/WhatsApp, IdP,
 * BI, and external accounting.
 *
 * Arabic and English (C7): every word from the catalogs; the success rate is
 * a percentage and the last run a date and time the locale writes. Connector
 * names and categories are the connector registry's own.
 */
import { useId } from "react";
import {
  Card,
  PageHeader,
  Badge,
  DataTable,
  Skeleton,
  StatusBadge,
} from "@/components/ui";
import { PendingButton, PendingNote } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { formatDateTime, formatPercent } from "@/lib/money";

interface Connector {
  id: string;
  name: string;
  category: string;
  status: "Connected" | "Error" | "NotConfigured" | "Disabled";
  successRate: number;
  lastRunAt: string | null;
}

export function IntegrationCenterPage() {
  const { t } = useTranslation();
  const pendingId = useId();
  const { data, isLoading } = useApiList<Connector>(
    ["integrations", "connectors"],
    "/integrations/connectors",
  );

  return (
    <div>
      <PageHeader
        title={t("admin.integrations.title")}
        subtitle={t("admin.integrations.subtitle")}
        actions={
          <PendingButton variant="primary" reasonId={pendingId}>
            {t("admin.integrations.add")}
          </PendingButton>
        }
      />
      {/* Every connector is a mock today and none is registered (CLAUDE.md,
          Known state); real connectors are roadmap F23, after the launch. */}
      <PendingNote id={pendingId} reason="afterLaunch" slice="F23" />
      {isLoading || !data ? (
        <Skeleton rows={8} />
      ) : (
        <Card title={listTitle(t("admin.integrations.list"), data.total)}>
          <DataTable<Connector>
            rows={data.items}
            columns={[
              { key: "name", label: t("admin.integrations.connector") },
              {
                key: "category",
                label: t("fields.category"),
                render: (r) => <Badge tone="default">{r.category}</Badge>,
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) => (
                  <StatusBadge status={r.status} type="Connector" />
                ),
              },
              {
                key: "successRate",
                label: t("admin.integrations.successRate"),
                numeric: true,
                render: (r) => formatPercent(r.successRate),
              },
              {
                key: "lastRunAt",
                label: t("admin.integrations.lastRun"),
                render: (r) => formatDateTime(r.lastRunAt),
              },
              {
                key: "id",
                label: t("fields.actions"),
                render: (r) =>
                  r.status === "NotConfigured" ? (
                    <PendingButton
                      size="sm"
                      variant="primary"
                      reasonId={pendingId}
                    >
                      {t("admin.integrations.configure")}
                    </PendingButton>
                  ) : (
                    <PendingButton size="sm" reasonId={pendingId}>
                      {t("admin.integrations.settings")}
                    </PendingButton>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
